
export interface SATTopic {
  id: string;
  name: string;
  subtopics: string[];
  questionCount: string;
  weight: string;
  difficulty: 'Foundation' | 'Intermediate' | 'Advanced';
}

export interface SATSection {
  id: string;
  name: string;
  icon: string;
  color: string;
  bgColor: string;
  totalQuestions: number;
  timeMinutes: number;
  scoreRange: string;
  description: string;
  domains: SATDomain[];
}

export interface SATDomain {
  id: string;
  name: string;
  icon: string;
  color: string;
  percentage: string;
  questionRange: string;
  overview: string;
  topics: SATTopic[];
  keySkills: string[];
  testTips: string[];
}

/* -------------------------------------------------------------------------- */
/* 1️⃣  Primary array of sections (used for ordered iteration)                */
/* -------------------------------------------------------------------------- */
export const satSections: SATSection[] = [
  {
    id: 'math',
    name: 'SAT Math',
    icon: 'ri-calculator-line',
    color: 'from-emerald-600 to-teal-600',
    bgColor: 'emerald',
    totalQuestions: 44,
    timeMinutes: 70,
    scoreRange: '200–800',
    description:
      'Tests algebra, advanced math, problem-solving, data analysis, and geometry. Calculator is permitted for the entire Math section on the digital SAT.',
    domains: [
      {
        id: 'algebra',
        name: 'Algebra',
        icon: 'ri-function-line',
        color: 'emerald',
        percentage: '35%',
        questionRange: '~13–15 questions',
        overview:
          'The Algebra domain is the largest on the SAT Math section. It covers linear equations, inequalities, systems, and their real-world applications. Mastery here is essential for a high score.',
        topics: [
          {
            id: 'linear-equations-one-var',
            name: 'Linear Equations in One Variable',
            subtopics: [
              'Solving one-step and multi-step linear equations',
              'Equations with variables on both sides',
              'Equations with fractions and decimals',
              'Literal equations (solving for a specific variable)',
              'No solution and infinitely many solutions',
              'Word problems: age, distance, mixture, consecutive integers',
            ],
            questionCount: '3–4',
            weight: 'High',
            difficulty: 'Foundation',
          },
          {
            id: 'linear-functions',
            name: 'Linear Functions',
            subtopics: [
              'Slope-intercept form: y = mx + b',
              'Point-slope form: y − y₁ = m(x − x₁)',
              'Standard form: Ax + By = C',
              'Interpreting slope as rate of change',
              'Interpreting y-intercept as initial value',
              'Writing equations from two points or a table',
              'Parallel and perpendicular lines',
              'Graphing linear functions and identifying key features',
            ],
            questionCount: '3–4',
            weight: 'High',
            difficulty: 'Foundation',
          },
          {
            id: 'linear-inequalities',
            name: 'Linear Inequalities',
            subtopics: [
              'Solving and graphing one-variable inequalities',
              'Compound inequalities (AND / OR)',
              'Absolute value inequalities',
              'Two-variable linear inequalities and their graphs',
              'Systems of inequalities',
              'Real-world constraint problems',
            ],
            questionCount: '2–3',
            weight: 'Medium',
            difficulty: 'Intermediate',
          },
          {
            id: 'systems-linear',
            name: 'Systems of Linear Equations & Inequalities',
            subtopics: [
              'Solving by substitution',
              'Solving by elimination (addition/subtraction)',
              'Graphical interpretation of solutions',
              'No solution vs. infinitely many solutions',
              'Systems of inequalities — feasible region',
              'Word problems: cost, rate, mixture, investment',
            ],
            questionCount: '3–4',
            weight: 'High',
            difficulty: 'Intermediate',
          },
        ],
        keySkills: [
          'Translate word problems into algebraic equations',
          'Identify when a system has no solution or infinite solutions',
          'Interpret slope and intercept in context',
          'Solve multi-step equations efficiently',
        ],
        testTips: [
          'Always check your answer by substituting back into the original equation.',
          'For word problems, define your variable clearly before writing the equation.',
          'When solving systems, choose elimination when coefficients are easy to match.',
          'Watch for "at least," "no more than," and "between" — these signal inequalities.',
        ],
      },
      {
        id: 'advanced-math',
        name: 'Advanced Math',
        icon: 'ri-bar-chart-grouped-line',
        color: 'teal',
        percentage: '35%',
        questionRange: '~13–15 questions',
        overview:
          'Advanced Math covers nonlinear equations, quadratics, polynomials, exponential functions, and rational expressions. These are the most challenging questions on the SAT Math section.',
        topics: [
          {
            id: 'quadratic-equations',
            name: 'Quadratic Equations & Functions',
            subtopics: [
              'Standard form: ax² + bx + c = 0',
              'Factoring: GCF, trinomials, difference of squares',
              'Completing the square',
              'Quadratic formula: x = (−b ± √(b²−4ac)) / 2a',
              'Discriminant: b²−4ac (nature of roots)',
              'Vertex form: a(x − h)² + k',
              'Graphing parabolas: vertex, axis of symmetry, intercepts',
              'Transformations of quadratic functions',
              'Quadratic word problems (projectile motion, area)',
            ],
            questionCount: '4–5',
            weight: 'Very High',
            difficulty: 'Advanced',
          },
          {
            id: 'polynomials',
            name: 'Polynomials & Polynomial Equations',
            subtopics: [
              'Adding, subtracting, multiplying polynomials',
              'Polynomial long division',
              'Synthetic division',
              'Remainder theorem and factor theorem',
              'Zeros/roots of polynomials',
              'End behavior of polynomial functions',
              'Factoring higher-degree polynomials',
            ],
            questionCount: '2–3',
            weight: 'Medium',
            difficulty: 'Advanced',
          },
          {
            id: 'exponential-functions',
            name: 'Exponential Functions',
            subtopics: [
              'Exponential growth: f(x) = a · bˣ, b > 1',
              'Exponential decay: f(x) = a · bˣ, 0 < b < 1',
              'Percent increase/decrease models',
              'Compound interest: A = P(1 + r/n)^(nt)',
              'Comparing linear vs. exponential growth',
              'Interpreting exponential equations in context',
            ],
            questionCount: '2–3',
            weight: 'Medium',
            difficulty: 'Intermediate',
          },
          {
            id: 'rational-expressions',
            name: 'Rational Expressions & Equations',
            subtopics: [
              'Simplifying rational expressions',
              'Multiplying and dividing rational expressions',
              'Adding and subtracting rational expressions (LCD)',
              'Solving rational equations',
              'Extraneous solutions',
              'Complex fractions',
            ],
            questionCount: '1–2',
            weight: 'Medium',
            difficulty: 'Advanced',
          },
          {
            id: 'radical-equations',
            name: 'Radical & Absolute Value Equations',
            subtopics: [
              'Simplifying radical expressions',
              'Solving radical equations (squaring both sides)',
              'Checking for extraneous solutions',
              'Rational exponents: x^(m/n)',
              'Absolute value equations: |ax + b| = c',
              'Absolute value functions and their graphs',
            ],
            questionCount: '1–2',
            weight: 'Medium',
            difficulty: 'Advanced',
          },
          {
            id: 'function-notation',
            name: 'Function Notation & Operations',
            subtopics: [
              'Function notation: f(x), g(x)',
              'Evaluating functions',
              'Domain and range',
              'Composition of functions: f(g(x))',
              'Inverse functions',
              'Piecewise functions',
              'Transformations: shifts, reflections, stretches',
            ],
            questionCount: '2–3',
            weight: 'High',
            difficulty: 'Intermediate',
          },
        ],
        keySkills: [
          'Factor quadratics quickly using multiple methods',
          'Identify the vertex and intercepts of a parabola',
          'Recognize exponential growth vs. decay from an equation',
          'Evaluate and compose functions',
        ],
        testTips: [
          'Memorize the quadratic formula — it appears on almost every SAT.',
          'For exponential models, identify the initial value (a) and growth/decay factor (b) first.',
          'When dividing polynomials, check if the factor theorem applies first.',
          'Always check for extraneous solutions when solving radical or rational equations.',
        ],
      },
      {
        id: 'problem-solving-data',
        name: 'Problem-Solving & Data Analysis',
        icon: 'ri-pie-chart-line',
        color: 'cyan',
        percentage: '15%',
        questionRange: '~5–7 questions',
        overview:
          'This domain tests your ability to interpret data, use ratios and proportions, understand statistics, and analyze graphs and tables. These questions are often set in real-world contexts.',
        topics: [
          {
            id: 'ratios-proportions',
            name: 'Ratios, Rates & Proportions',
            subtopics: [
              'Setting up and solving proportions',
              'Unit rates and unit conversions',
              'Scale factors and similar figures',
              'Percent problems: percent of, percent change, percent error',
              'Direct and inverse variation',
              'Multi-step ratio problems',
            ],
            questionCount: '1–2',
            weight: 'Medium',
            difficulty: 'Foundation',
          },
          {
            id: 'statistics',
            name: 'Statistics & Data Interpretation',
            subtopics: [
              'Mean, median, mode, range',
              'Weighted averages',
              'Standard deviation (conceptual understanding)',
              'Reading bar graphs, histograms, dot plots, box plots',
              'Interpreting scatter plots and lines of best fit',
              'Two-way tables and conditional probability',
              'Margin of error and sampling methods',
            ],
            questionCount: '2–3',
            weight: 'High',
            difficulty: 'Intermediate',
          },
          {
            id: 'probability',
            name: 'Probability',
            subtopics: [
              'Basic probability: P(A) = favorable/total',
              'Complementary events: P(not A) = 1 − P(A)',
              'Conditional probability',
              'Independent vs. dependent events',
              'Probability from two-way tables',
            ],
            questionCount: '1–2',
            weight: 'Medium',
            difficulty: 'Intermediate',
          },
        ],
        keySkills: [
          'Read and interpret graphs, tables, and charts accurately',
          'Calculate and interpret mean, median, and standard deviation',
          'Set up proportions from word problems',
          'Use two-way tables to find conditional probabilities',
        ],
        testTips: [
          'For data questions, read axis labels and units carefully before answering.',
          'Median is more resistant to outliers than mean — the SAT tests this concept.',
          'For percent change: (new − old) / old × 100.',
          'Two-way table questions often ask for conditional probability — divide by the row or column total, not the grand total.',
        ],
      },
      {
        id: 'geometry-trig',
        name: 'Geometry & Trigonometry',
        icon: 'ri-shape-line',
        color: 'amber',
        percentage: '15%',
        questionRange: '~5–7 questions',
        overview:
          'Geometry and Trigonometry questions test area, volume, coordinate geometry, and right triangle trigonometry. The SAT provides a reference sheet with key formulas.',
        topics: [
          {
            id: 'area-volume',
            name: 'Area, Perimeter & Volume',
            subtopics: [
              'Area of triangles, rectangles, parallelograms, trapezoids',
              'Area and circumference of circles',
              'Area of composite figures',
              'Volume of prisms, cylinders, cones, spheres, pyramids',
              'Surface area of 3D figures',
              'Scale factor and its effect on area/volume',
            ],
            questionCount: '1–2',
            weight: 'Medium',
            difficulty: 'Foundation',
          },
          {
            id: 'coordinate-geometry',
            name: 'Coordinate Geometry',
            subtopics: [
              'Distance formula: d = √((x₂−x₁)² + (y₂−y₁)²)',
              'Midpoint formula: ((x₁+x₂)/2, (y₁+y₂)/2)',
              'Equation of a circle: (x−h)² + (y−k)² = r²',
              'Completing the square to find center and radius',
              'Graphing circles and identifying key features',
            ],
            questionCount: '1–2',
            weight: 'Medium',
            difficulty: 'Intermediate',
          },
          {
            id: 'triangles-trig',
            name: 'Triangles & Trigonometry',
            subtopics: [
              'Pythagorean theorem: a² + b² = c²',
              'Special right triangles: 30-60-90 and 45-45-90',
              'Similar triangles and proportionality',
              'SOH-CAH-TOA: sin, cos, tan',
              'Trigonometric ratios in right triangles',
              'Complementary angle relationships: sin θ = cos(90°−θ)',
              'Angles of elevation and depression',
            ],
            questionCount: '2–3',
            weight: 'High',
            difficulty: 'Intermediate',
          },
          {
            id: 'lines-angles',
            name: 'Lines, Angles & Polygons',
            subtopics: [
              'Vertical angles, supplementary, complementary',
              'Parallel lines cut by a transversal',
              'Interior and exterior angles of polygons',
              'Properties of quadrilaterals',
              'Congruence and similarity',
            ],
            questionCount: '1–2',
            weight: 'Medium',
            difficulty: 'Foundation',
          },
        ],
        keySkills: [
          'Apply the Pythagorean theorem and special right triangles',
          'Use SOH-CAH-TOA to find missing sides and angles',
          'Calculate area and volume of standard figures',
          'Use the distance and midpoint formulas in coordinate geometry',
        ],
        testTips: [
          'The SAT provides a formula reference sheet — know what\'s on it so you don\'t waste time.',
          'For circle equations, always complete the square if the equation is not in standard form.',
          'Special right triangles (30-60-90 and 45-45-90) appear frequently — memorize the ratios.',
          'Draw and label diagrams for every geometry problem.',
        ],
      },
    ],
  },
  {
    id: 'english',
    name: 'SAT Reading & Writing',
    icon: 'ri-book-open-line',
    color: 'from-rose-600 to-pink-600',
    bgColor: 'rose',
    totalQuestions: 54,
    timeMinutes: 64,
    scoreRange: '200–800',
    description:
      'Tests reading comprehension, vocabulary in context, grammar, editing, and rhetorical skills. Passages come from literature, history, social science, and natural science.',
    domains: [
      {
        id: 'information-ideas',
        name: 'Information & Ideas',
        icon: 'ri-article-line',
        color: 'rose',
        percentage: '26%',
        questionRange: '~12–14 questions',
        overview:
          'These questions test your ability to read carefully, identify main ideas, understand details, and draw logical inferences from passages across multiple subject areas.',
        topics: [
          {
            id: 'central-ideas',
            name: 'Central Ideas & Details',
            subtopics: [
              'Identifying the main idea or central claim of a passage',
              'Distinguishing main idea from supporting details',
              'Summarizing a passage or paragraph accurately',
              'Identifying explicit vs. implicit information',
              'Understanding the relationship between ideas',
              'Recognizing supporting evidence for a claim',
            ],
            questionCount: '3–4',
            weight: 'High',
            difficulty: 'Foundation',
          },
          {
            id: 'inferences',
            name: 'Inferences & Conclusions',
            subtopics: [
              'Drawing logical inferences from text evidence',
              'Identifying what can and cannot be concluded',
              'Understanding implied meaning',
              'Distinguishing between what is stated and what is implied',
              'Using context to make reasonable predictions',
            ],
            questionCount: '3–4',
            weight: 'High',
            difficulty: 'Intermediate',
          },
          {
            id: 'command-of-evidence',
            name: 'Command of Evidence',
            subtopics: [
              'Identifying textual evidence that supports a claim',
              'Evaluating the strength of evidence',
              'Paired questions: claim + best supporting quote',
              'Interpreting data from graphs, tables, and charts within passages',
              'Connecting quantitative data to textual claims',
              'Identifying evidence that weakens or contradicts a claim',
            ],
            questionCount: '4–5',
            weight: 'Very High',
            difficulty: 'Advanced',
          },
        ],
        keySkills: [
          'Read actively and annotate key claims and evidence',
          'Distinguish between what the author states and what can be inferred',
          'Connect data in graphs/tables to the passage\'s argument',
          'Evaluate whether evidence actually supports a given claim',
        ],
        testTips: [
          'Always return to the passage to verify your answer — never rely on memory alone.',
          'For "best evidence" questions, the correct answer must directly support the previous question\'s answer.',
          'Main idea questions: eliminate answers that are too broad, too narrow, or off-topic.',
          'Inference questions: the correct answer must be logically supported — not just possible.',
        ],
      },
      {
        id: 'craft-structure',
        name: 'Craft & Structure',
        icon: 'ri-quill-pen-line',
        color: 'pink',
        percentage: '28%',
        questionRange: '~13–15 questions',
        overview:
          'Craft & Structure is the largest domain in Reading & Writing. It tests vocabulary in context, text structure, author\'s purpose, and cross-text analysis.',
        topics: [
          {
            id: 'vocabulary-context',
            name: 'Words in Context (Vocabulary)',
            subtopics: [
              'Determining the meaning of words and phrases in context',
              'Choosing the most precise word for a given context',
              'Understanding connotation vs. denotation',
              'Academic vocabulary: Tier 2 and Tier 3 words',
              'Figurative language: metaphor, simile, personification, irony',
              'Tone words: critical, skeptical, enthusiastic, ambivalent, etc.',
            ],
            questionCount: '4–5',
            weight: 'Very High',
            difficulty: 'Intermediate',
          },
          {
            id: 'text-structure-purpose',
            name: 'Text Structure & Purpose',
            subtopics: [
              'Identifying the overall structure of a passage (compare/contrast, cause/effect, problem/solution, chronological)',
              'Understanding the function of a specific paragraph or sentence',
              'Identifying the author\'s purpose (to inform, persuade, entertain, describe)',
              'Recognizing rhetorical strategies: anecdote, analogy, appeal to authority',
              'Understanding how structure supports the author\'s argument',
            ],
            questionCount: '3–4',
            weight: 'High',
            difficulty: 'Intermediate',
          },
          {
            id: 'cross-text-connections',
            name: 'Cross-Text Connections',
            subtopics: [
              'Comparing two passages on the same topic',
              'Identifying agreement and disagreement between authors',
              'Understanding how one author would respond to another\'s argument',
              'Synthesizing information from multiple sources',
              'Identifying shared assumptions or differing perspectives',
            ],
            questionCount: '2–3',
            weight: 'Medium',
            difficulty: 'Advanced',
          },
          {
            id: 'authors-purpose-tone',
            name: "Author's Purpose, Tone & Point of View",
            subtopics: [
              'Identifying the author\'s attitude toward the subject',
              'Recognizing tone: objective, critical, admiring, ironic, nostalgic',
              'Understanding point of view (first, second, third person)',
              'Identifying bias and perspective',
              'Distinguishing fact from opinion',
            ],
            questionCount: '2–3',
            weight: 'High',
            difficulty: 'Intermediate',
          },
        ],
        keySkills: [
          'Use context clues to determine word meaning without a dictionary',
          'Identify how a passage is organized and why',
          'Compare two authors\' perspectives on the same topic',
          'Recognize tone and purpose from word choice and structure',
        ],
        testTips: [
          'For vocabulary questions, always read the full sentence — the correct answer fits the specific context, not just the general meaning.',
          'Cross-text questions: read both passages before answering — don\'t assume they agree.',
          'Purpose questions: ask yourself "Why did the author include this?" not just "What does it say?"',
          'Tone words: build a vocabulary list of common tone descriptors (sardonic, reverent, wistful, etc.).',
        ],
      },
      {
        id: 'expression-of-ideas',
        name: 'Expression of Ideas',
        icon: 'ri-edit-2-line',
        color: 'fuchsia',
        percentage: '20%',
        questionRange: '~9–11 questions',
        overview:
          'Expression of Ideas questions test your ability to revise and improve writing. You\'ll choose the best way to express an idea, add or delete information, and organize content effectively.',
        topics: [
          {
            id: 'rhetorical-synthesis',
            name: 'Rhetorical Synthesis',
            subtopics: [
              'Combining notes or bullet points into a coherent sentence',
              'Choosing the most effective way to present information',
              'Selecting the best introduction or conclusion for a passage',
              'Adding a sentence that best supports the main argument',
              'Identifying the most logical transition between ideas',
            ],
            questionCount: '4–5',
            weight: 'Very High',
            difficulty: 'Intermediate',
          },
          {
            id: 'transitions',
            name: 'Transitions & Logical Flow',
            subtopics: [
              'Choosing the correct transition word or phrase',
              'Contrast transitions: however, although, despite, on the other hand',
              'Addition transitions: furthermore, in addition, moreover',
              'Cause/effect transitions: therefore, as a result, consequently',
              'Sequence transitions: first, then, finally, subsequently',
              'Ensuring logical flow between sentences and paragraphs',
            ],
            questionCount: '3–4',
            weight: 'High',
            difficulty: 'Foundation',
          },
        ],
        keySkills: [
          'Identify the most logical and effective way to combine ideas',
          'Choose transitions that accurately reflect the relationship between ideas',
          'Evaluate whether a sentence should be added, deleted, or revised',
          'Recognize when information is redundant or off-topic',
        ],
        testTips: [
          'For transition questions, identify the relationship between the two ideas first (contrast? addition? cause/effect?), then choose the matching transition.',
          'Rhetorical synthesis: the correct answer addresses the specific purpose stated in the question stem.',
          'When adding a sentence, ask: does this directly support the paragraph\'s main point?',
          'Avoid answers that introduce new, unrelated information.',
        ],
      },
      {
        id: 'standard-english',
        name: 'Standard English Conventions',
        icon: 'ri-spell-check-line',
        color: 'violet',
        percentage: '26%',
        questionRange: '~12–14 questions',
        overview:
          'These questions test grammar, punctuation, and sentence structure. You\'ll correct errors in subject-verb agreement, pronoun usage, punctuation, and sentence construction.',
        topics: [
          {
            id: 'sentence-structure',
            name: 'Sentence Structure & Boundaries',
            subtopics: [
              'Identifying and correcting run-on sentences',
              'Identifying and correcting sentence fragments',
              'Comma splices and how to fix them',
              'Coordinating conjunctions: FANBOYS (for, and, nor, but, or, yet, so)',
              'Subordinating conjunctions: because, although, since, while, when',
              'Semicolons to join independent clauses',
              'Colons to introduce a list or explanation',
            ],
            questionCount: '3–4',
            weight: 'High',
            difficulty: 'Foundation',
          },
          {
            id: 'punctuation',
            name: 'Punctuation',
            subtopics: [
              'Commas: introductory elements, items in a series, nonrestrictive clauses',
              'Commas with coordinating conjunctions',
              'Apostrophes: possessives (singular and plural) vs. contractions',
              'Semicolons and colons: correct usage',
              'Dashes: em dash for emphasis or interruption',
              'Avoiding unnecessary punctuation',
            ],
            questionCount: '3–4',
            weight: 'High',
            difficulty: 'Intermediate',
          },
          {
            id: 'subject-verb-agreement',
            name: 'Subject-Verb Agreement',
            subtopics: [
              'Singular vs. plural subjects and verbs',
              'Intervening phrases (subject is not the noun closest to the verb)',
              'Indefinite pronouns: everyone, each, neither (singular)',
              'Compound subjects with "and" (plural) vs. "or/nor" (match nearest)',
              'Collective nouns: team, group, committee (usually singular)',
              'Inverted sentences (verb before subject)',
            ],
            questionCount: '2–3',
            weight: 'High',
            difficulty: 'Intermediate',
          },
          {
            id: 'pronoun-usage',
            name: 'Pronoun Usage & Agreement',
            subtopics: [
              'Pronoun-antecedent agreement (singular/plural)',
              'Pronoun case: subjective (I, he, she, they), objective (me, him, her, them)',
              'Reflexive pronouns: myself, himself, themselves',
              'Ambiguous pronoun reference',
              'Who vs. whom',
              'That vs. which (restrictive vs. nonrestrictive clauses)',
            ],
            questionCount: '2–3',
            weight: 'Medium',
            difficulty: 'Intermediate',
          },
          {
            id: 'modifiers',
            name: 'Modifiers & Parallelism',
            subtopics: [
              'Dangling modifiers: the modifier must logically refer to the subject',
              'Misplaced modifiers: place modifiers close to what they modify',
              'Parallel structure in lists and comparisons',
              'Parallel structure with correlative conjunctions: both...and, either...or, not only...but also',
              'Adjective vs. adverb usage',
            ],
            questionCount: '2–3',
            weight: 'Medium',
            difficulty: 'Advanced',
          },
          {
            id: 'verb-tense',
            name: 'Verb Tense, Mood & Voice',
            subtopics: [
              'Consistent verb tense within a passage',
              'Simple, progressive, and perfect tenses',
              'Subjunctive mood: "If I were..." (not "was")',
              'Active vs. passive voice',
              'Sequence of tenses in complex sentences',
            ],
            questionCount: '1–2',
            weight: 'Medium',
            difficulty: 'Intermediate',
          },
        ],
        keySkills: [
          'Identify and fix run-ons, fragments, and comma splices',
          'Use commas, semicolons, colons, and apostrophes correctly',
          'Match subjects and verbs even when separated by phrases',
          'Ensure pronouns agree with their antecedents',
        ],
        testTips: [
          'For subject-verb agreement, cross out the intervening phrase to find the true subject.',
          'Apostrophe rule: if you can say "it is," use "it\'s"; otherwise use "its."',
          'Semicolons work like periods — both sides must be complete sentences.',
          'For modifier questions, ask: who or what is performing the action in the opening phrase?',
          'Parallel structure: all items in a list must be the same grammatical form.',
        ],
      },
    ],
  },
];

/* -------------------------------------------------------------------------- */
/* 2️⃣  Helper map for quick lookup by section id (renamed to avoid conflict) */
/* -------------------------------------------------------------------------- */
export const satSectionMap: Record<string, SATSection> = {
  math: satSections.find((s) => s.id === 'math')!,
  english: satSections.find((s) => s.id === 'english')!,
};

/* -------------------------------------------------------------------------- */
/* 3️⃣  Overview data – unchanged                                            */
/* -------------------------------------------------------------------------- */
export const satOverview = {
  totalTime: '2 hours 14 minutes',
  totalQuestions: 98,
  sections: [
    { name: 'Reading & Writing', modules: 2, questionsEach: 27, timeEach: '32 min' },
    { name: 'Math', modules: 2, questionsEach: 22, timeEach: '35 min' },
  ],
  scoreRange: '400–1600',
  format: 'Digital Adaptive Test (DSAT)',
  adaptive: 'Module 2 difficulty adapts based on Module 1 performance',
};
